import { EventContextProvider, useAnalytics } from "@codat/analytics";
import { ErrorDialog, Tooltip, TooltipContent } from "@codat/orchard-ui";
import { useQueryClient } from "@tanstack/react-query";
import { GetCompanyResponse, useGetCompaniesQuery } from "api/public";
import { BannerType, useBannerContext } from "contexts/BannerContext";
import { useLocalStorage } from "contexts/LocalStorageContext";
import { useProducts } from "contexts/ProductsContext";
import { spendInsightsCompaniesKeys } from "hooks/query/useSpendInsightsCompanies";
import { usePageNumberRouting } from "hooks/usePageNumberRouting";
import { useFeatureStatus } from "portal-ui-remote/FeatureStatusContext";
import { useUserState } from "portal-ui-remote/UserStateContext";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useCompanyLimitReached } from "utils/getTotalConnections";
import { objectKeys } from "utils/typescript";

import Button from "components/Button";
import CompanySearch from "components/CompanySearch";
import Modal from "components/Modal";
import Page from "components/Page";
import PageHeader from "components/PageHeader";
import UpgradeButton from "components/UpgradeButton";

import styles from "./Companies.module.scss";
import { ConnectionStatusDropdown } from "./ConnectionStatusDropdown";
import DeleteCompanyModal from "./DeleteCompanyModal";
import EditCompanyModal, { EditCompanyFormState } from "./EditCompanyModal";
import { useRecentlyCreatedCompanies } from "./hooks/useRecentlyCreatedCompanies";
import InviteCompanyModal from "./InviteCompanyModal";
import LinkModal from "./LinkModal";
import NewCompanyModal from "./NewCompanyModal";
import CompaniesTable from "./Tables/CompaniesTable/CompaniesTable";
import {
  CreatedCompaniesTable,
  MAX_COMPANIES_TO_SHOW,
} from "./Tables/CreatedCompaniesTable/CreatedCompaniesTable";
import {
  buildDataConnectionsQuery,
  buildSearchQuery,
  combineApiQueries,
  mapParamsToState,
} from "./utils/filters";

export type CompaniesSortKey =
  | "name"
  | "created"
  | "lastSync"
  | "cta"
  | "connections";

export type SourceType =
  | "Accounting"
  | "Banking"
  | "Commerce"
  | "Unknown"
  | "BankFeed"
  | "Expense"
  | "Other";

export type ConnectionStatus =
  | "Linked"
  | "Unlinked"
  | "PendingAuth"
  | "Deauthorized";

export type ConnectionStatusFilterState = Record<ConnectionStatus, boolean>;

export interface EditCompanyModalState {
  open: boolean;
  company?: EditCompanyFormState;
  onClose?: () => void;
}

const initialDataConnectionFilterState = {
  Linked: true,
  Unlinked: true,
  PendingAuth: true,
  Deauthorized: true,
};

function Companies() {
  const { sendEvent } = useAnalytics();
  const { products } = useProducts();
  const queryClient = useQueryClient();
  const { addBanner, removeBanner } = useBannerContext();
  const { userState } = useUserState();
  const location = useLocation();
  const history = useHistory();
  const localStorageService = useLocalStorage();
  const [dataConnectionFilterState, setDataConnectionFilterState] = useState(
    initialDataConnectionFilterState
  );
  const recentlyCreatedCompanies = useRecentlyCreatedCompanies({
    pageSize: MAX_COMPANIES_TO_SHOW,
  });
  const [searchInputValue, setSearchInputValue] = useState("");
  const [debouncedSearchInput, setDebouncedSearchInput] = useState("");
  const [orderBy, setOrderBy] = useState<CompaniesSortKey | string | undefined>(
    localStorageService.orderBy() || "created"
  );
  const [orderByDirection, setOrderByDirection] = useState<
    "asc" | "desc" | null
  >(localStorageService.orderByDirection() || "desc");
  const [showTooltip, setShowTooltip] = useState(false);
  const { featureStatus } = useFeatureStatus();

  const clearModalSearchParams = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete("newCompany");
    searchParams.delete("newCompanyProducts");
    history.push({ search: searchParams.toString() });
  }, [history, location.search]);

  useEffect(() => {
    const search = new URLSearchParams(location.search);
    const dataConnectionStatus = search.get("dataConnectionStatus");

    if (!dataConnectionStatus) {
      setDataConnectionFilterState(initialDataConnectionFilterState);
      return;
    }

    setDataConnectionFilterState(mapParamsToState(search));
  }, [location.search]);

  const hasDataConnectionStatusFilter = ![
    0,
    Object.keys(dataConnectionFilterState).length,
  ].includes(Object.values(dataConnectionFilterState).filter(Boolean).length);

  const updateFilterState = (state: ConnectionStatusFilterState) => {
    const search = new URLSearchParams(location.search);
    const keys = objectKeys(state);

    const newQuery =
      // If all are selected or none are selected, remove the query param
      keys.filter((k) => state[k]).length === keys.length || keys.length === 0
        ? ""
        : keys.filter((k) => state[k]).join("||");

    if (newQuery) {
      search.set("dataConnectionStatus", newQuery);
    } else {
      search.delete("dataConnectionStatus");
    }
    // Reset to first page
    search.set("page", "1");

    setDataConnectionFilterState(state);
    history.replace({
      search: search.toString(),
    });
  };

  const query = useMemo(() => {
    const searchQuery = buildSearchQuery(debouncedSearchInput);
    const dataConnectionsQuery = buildDataConnectionsQuery(
      dataConnectionFilterState
    );
    return combineApiQueries(searchQuery, dataConnectionsQuery);
  }, [debouncedSearchInput, dataConnectionFilterState]);

  const orderByWithDirection = useMemo(() => {
    if (orderBy === "name") {
      // without this the companies would show from Z - A first
      return orderByDirection === "desc" ? orderBy : `-${orderBy}`;
    }
    return orderByDirection === "desc" ? `-${orderBy}` : orderBy;
  }, [orderBy, orderByDirection]);

  const { page, setPage } = usePageNumberRouting();

  const [pageSize, setPageSize] = useState(
    () => localStorageService.pageSize() ?? 10
  );

  useEffect(() => {
    localStorageService.setPageSize(pageSize);
  }, [pageSize, localStorageService]);

  useEffect(() => {
    orderBy && localStorageService.setOrderBy(orderBy);
    orderByDirection &&
      localStorageService.setOrderByDirection(orderByDirection);
  }, [orderBy, localStorageService, orderByDirection]);

  const [{ data, loading, error }, refetchCompanies] = useGetCompaniesQuery({
    queryParams: {
      page,
      pageSize,
      orderBy: orderByWithDirection,
      query,
    },
  });

  const [linkModalData, setLinkModalData] = useState<GetCompanyResponse | null>(
    null
  );
  const [inviteCompanyLinkUrl, setInviteCompanyLinkUrl] = useState("");
  const [inviteCompanyModal, setInviteCompanyModal] = useState(false);

  const openInviteCompanyModal = () => setInviteCompanyModal(true);
  const closeInviteCompanyModal = useCallback(
    () => setInviteCompanyModal(false),
    [setInviteCompanyModal]
  );

  const isCompanyLimitReached = useCompanyLimitReached(
    userState?.companyLimit,
    data?.totalResults ?? 0
  );
  const isUseNewLinkFlowEnabled =
    featureStatus?.["Link.UseNewLinkFlow"] ?? false;
  const disableInviteButton = isCompanyLimitReached || isUseNewLinkFlowEnabled;

  const [newCompanyModal, setNewCompanyModal] = useState(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);

    if (loading) {
      return;
    }

    if (searchParams.get("newCompany") === "true") {
      if (isCompanyLimitReached) {
        clearModalSearchParams();
        return;
      } else {
        setNewCompanyModal(true);
      }
    } else {
      setNewCompanyModal(false);
    }
  }, [
    setNewCompanyModal,
    location.search,
    clearModalSearchParams,
    isCompanyLimitReached,
    loading,
  ]);

  const openNewCompanyModal = () => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("newCompany", "true");
    history.push({ search: searchParams.toString() });
  };

  const onAddCompany = (companyId: string) => {
    recentlyCreatedCompanies.setStoredValue([
      ...recentlyCreatedCompanies.storedValue,
      companyId,
    ]);
    refetchCompanies().catch(() => {
      // Fail silently
    });

    // invalidate spend insights table
    queryClient.invalidateQueries({
      queryKey: spendInsightsCompaniesKeys.all,
    });
  };

  const [deleteCompanyModal, setDeleteCompanyModal] = useState<{
    open: boolean;
    company?: { id: string; name: string };
  }>({ open: false });
  const openDeleteCompanyModal = (company: { id: string; name: string }) =>
    setDeleteCompanyModal({ open: true, company });

  const closeDeleteCompanyModal = () => setDeleteCompanyModal({ open: false });

  const onDeleteCompany = (companyId: string) => {
    // Data must be defined to reach this point from UI
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const { pageNumber, pageSize, totalResults } = data!;
    const isLastPage = pageNumber * pageSize >= totalResults;
    const itemsOnLastPage = totalResults % pageSize;

    const updatedCreatedCompanies = recentlyCreatedCompanies.storedValue.filter(
      (id: string) => id !== companyId
    );

    recentlyCreatedCompanies.setStoredValue(updatedCreatedCompanies);
    // if deleting the last item on a page, fetch the previous page so the user is not left with an empty table
    if (page > 1 && isLastPage && itemsOnLastPage === 1) {
      setPage(page - 1, { replace: true });
    } else {
      refetchCompanies().catch(() => {
        // Fail silently
      });
    }

    // invalidate spend insights table
    queryClient.invalidateQueries({
      queryKey: spendInsightsCompaniesKeys.all,
    });

    closeDeleteCompanyModal();
  };

  const [editCompanyModal, setEditCompanyModal] =
    useState<EditCompanyModalState>({ open: false });

  const openEditCompanyModal = (company: EditCompanyFormState) =>
    setEditCompanyModal({ open: true, company });

  const closeEditCompanyModal = () => setEditCompanyModal({ open: false });

  const handleCompanyEditSuccess = () => {
    closeEditCompanyModal();
    refetchCompanies().catch(() => {
      // Fail silently
    });
    // Set state to a new reference to force rerender of CreatedCompaniesTable and trigger refetch
    recentlyCreatedCompanies.refetch();
  };

  useEffect(() => {
    if (isCompanyLimitReached && featureStatus) {
      addBanner({
        type: BannerType.COMPANIES_LIMIT_REACHED,
        index: 2,
        value: {
          title: `Company limit for this client has been reached`,
          variant: "error",
          cta: (
            <UpgradeButton
              userState={userState}
              hideStartUpPlan={
                featureStatus?.["PortalUI.HideStartUpPlan"] ?? false
              }
            />
          ),
        },
      });

      return () => removeBanner(BannerType.COMPANIES_LIMIT_REACHED);
    }
  }, [
    isCompanyLimitReached,
    userState,
    addBanner,
    removeBanner,
    featureStatus,
  ]);

  const searchCompanies = useCallback(
    (searchValue: string) => {
      setPage(1, { replace: page === 1 });
      setDebouncedSearchInput(searchValue);
    },
    [page, setPage]
  );

  // Error on page load
  const err = !data && error;
  if (err) {
    return (
      <ErrorDialog
        statusCode={error?.response?.data?.statusCode}
        correlationId={error?.response?.data?.correlationId}
        message={error?.response?.data?.error}
      />
    );
  }

  const userCanAddCompany = userState?.userPermissions?.includes(
    "clients_companies_add"
  );

  const onColumnSort = (column: CompaniesSortKey) => {
    if (orderBy !== column) {
      setOrderBy(column);
      setOrderByDirection("asc");
    } else {
      if (!orderByDirection) {
        setOrderByDirection("asc");
      } else if (orderByDirection == "asc") {
        setOrderByDirection("desc");
      } else {
        setOrderByDirection(null);
        setOrderBy(undefined);
      }
    }

    sendEvent("Companies Table - Sort", {
      sortBy: column,
      direction: orderByDirection,
    });
  };

  const onEmptyStateCtaClick = () => {
    openNewCompanyModal();
    sendEvent("Table Empty State - Button - Click");
  };

  const showRecentlyCreatedCompanies =
    !error &&
    recentlyCreatedCompanies.storedValue.length > 0 &&
    !!recentlyCreatedCompanies.data?.totalResults &&
    !debouncedSearchInput &&
    !hasDataConnectionStatusFilter;

  return (
    <Page>
      <PageHeader header="Companies">
        <div className={styles.headerActionRow}>
          <div>
            <CompanySearch
              className={styles.searchInput}
              onDebouncedValueChange={searchCompanies}
              onChange={(value) => setSearchInputValue(value)}
              value={searchInputValue}
            />
            <ConnectionStatusDropdown
              checkedState={dataConnectionFilterState}
              onCheck={updateFilterState}
            />
            {userCanAddCompany && (
              <>
                <Tooltip
                  data-testid="disabled-invite-tooltip"
                  isOpen={disableInviteButton && showTooltip}
                  placement="top"
                  content={
                    <TooltipContent>
                      {isCompanyLimitReached
                        ? "You can't invite any more companies to this client."
                        : "Use company-specific Link URLs to invite companies to this client."}
                    </TooltipContent>
                  }
                >
                  <div
                    data-testid="invite-company-wrapper"
                    onMouseOver={() => setShowTooltip(true)}
                    onMouseOut={() => setShowTooltip(false)}
                  >
                    <Button
                      data-testid="invite-company"
                      label="Invite company"
                      onClick={openInviteCompanyModal}
                      isSkeleton={!userState}
                      disabled={disableInviteButton}
                      buttonName="Invite Company"
                      variant="secondary"
                    />
                  </div>
                </Tooltip>
                <Button
                  data-testid="new-company"
                  label="Create company"
                  onClick={openNewCompanyModal}
                  isSkeleton={!userState}
                  disabled={isCompanyLimitReached}
                  buttonName="New Company"
                />
              </>
            )}
          </div>
        </div>
      </PageHeader>
      {showRecentlyCreatedCompanies && (
        <CreatedCompaniesTable
          createdCompanies={recentlyCreatedCompanies.data}
          onRequestData={setLinkModalData}
          onEditButtonClick={openEditCompanyModal}
          onDeleteButtonClick={openDeleteCompanyModal}
          onPollForUpdates={recentlyCreatedCompanies.refetch}
        />
      )}
      {error ? (
        <ErrorDialog
          className={styles.error}
          statusCode={error?.response?.data?.statusCode}
          correlationId={error?.response?.data?.correlationId}
          message={error?.response?.data?.error}
        />
      ) : (
        <CompaniesTable
          companies={data}
          isSkeleton={loading}
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          onRequestData={setLinkModalData}
          onEditButtonClick={openEditCompanyModal}
          onDeleteButtonClick={openDeleteCompanyModal}
          searchQuery={debouncedSearchInput}
          onColumnSort={onColumnSort}
          sortDirection={orderByDirection}
          sortKey={orderBy as CompaniesSortKey | undefined}
          onZeroStateCtaClick={onEmptyStateCtaClick}
          onClearSearch={() => setSearchInputValue("")}
          hasDataConnectionStatusFilter={hasDataConnectionStatusFilter}
          onClearFilters={() => {
            setSearchInputValue("");
            updateFilterState(initialDataConnectionFilterState);
          }}
        />
      )}
      {linkModalData && (
        <EventContextProvider name="Link Modal">
          <LinkModal
            company={linkModalData}
            onClose={() => setLinkModalData(null)}
          />
        </EventContextProvider>
      )}
      {inviteCompanyModal && (
        <EventContextProvider name="Invite Company Modal">
          <Modal onClose={closeInviteCompanyModal} isPinned>
            <InviteCompanyModal
              onClose={closeInviteCompanyModal}
              linkUrl={inviteCompanyLinkUrl}
              onRetrieveLinkSuccess={setInviteCompanyLinkUrl}
              onRetrieveLinkError={closeInviteCompanyModal}
            />
          </Modal>
        </EventContextProvider>
      )}
      {newCompanyModal && products?.length > 0 && (
        <EventContextProvider name="New Company Modal">
          <NewCompanyModal
            onAddCompany={onAddCompany}
            onCancel={clearModalSearchParams}
            products={products}
          />
        </EventContextProvider>
      )}
      {deleteCompanyModal.company && (
        <EventContextProvider name="Delete Company Modal">
          <DeleteCompanyModal
            company={deleteCompanyModal.company}
            open={deleteCompanyModal.open}
            onCancel={closeDeleteCompanyModal}
            onSuccess={onDeleteCompany}
          />
        </EventContextProvider>
      )}
      {editCompanyModal.company && (
        <EventContextProvider name="Edit Company Name Modal">
          <EditCompanyModal
            company={editCompanyModal.company}
            onClose={closeEditCompanyModal}
            onSuccess={handleCompanyEditSuccess}
          />
        </EventContextProvider>
      )}
    </Page>
  );
}

export default Companies;
